import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { NOTICES_BACKEND_PLUGIN_ID } from './const';
import { NoticesApiError } from './errors';
import type { Notice, NoticeInput, NoticesApi, NoticeUpdate } from './types';

type BackendErrorBody = {
  error?: { name?: string; message?: string };
};

export class NoticesRestApi implements NoticesApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async request(path: string, init?: RequestInit): Promise<Response> {
    const baseUrl = await this.discoveryApi.getBaseUrl(
      NOTICES_BACKEND_PLUGIN_ID,
    );
    const response = await this.fetchApi.fetch(`${baseUrl}/${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...init?.headers,
      },
    });
    if (!response.ok) {
      throw await toApiError(response);
    }
    return response;
  }

  async listNotices(): Promise<Notice[]> {
    const response = await this.request('notices');
    const body = (await response.json()) as { notices: Notice[] };
    return body.notices;
  }

  async createNotice(input: NoticeInput): Promise<Notice> {
    const response = await this.request('notices', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.json();
  }

  async updateNotice(id: string, update: NoticeUpdate): Promise<Notice> {
    const response = await this.request(`notices/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(update),
    });
    return response.json();
  }

  async deleteNotice(id: string): Promise<void> {
    await this.request(`notices/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async dismissNotice(id: string): Promise<void> {
    await this.request(`notices/${encodeURIComponent(id)}/dismiss`, {
      method: 'POST',
    });
  }
}

async function toApiError(response: Response): Promise<NoticesApiError> {
  let message = response.statusText || `Request failed`;
  try {
    const body = (await response.json()) as BackendErrorBody;
    if (body.error?.message) {
      message = body.error.message;
    }
  } catch {
    // The body is not JSON, so the status text stays.
  }
  return new NoticesApiError(message, response.status);
}
